import { OfferLettersService } from 'api-clients/monolith/services/OfferLettersService';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { MutationOptions, useApiServiceMutation } from 'hooks/useApiServiceMutation';

import { serverErrorMessage } from '../errors';
import messages from '../messages';
import { EditedKeys } from '../types';

export type OfferData = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiApplicantsOfferLetters>
>;

type OfferOptions = MutationOptions<OfferData, unknown>;

export interface OfferValuesInput {
  offer_letter_template_id?: string;
  /** Template field key -> value. */
  field_values?: Record<string, string>;
  /**
   * Data key -> value, for the keys driving the template's conditional sections
   * (AXHE-4001). A separate map from `field_values` because it addresses a
   * different key space. Omitting the key on an update leaves the stored answers
   * alone; an empty object clears them.
   */
  condition_values?: Record<string, string>;
  /**
   * Which controls the recruiter moved off the value the form displayed
   * (AXHE-4673) — see `SendOffer/values.ts#editedKeys`. Write-back to the
   * applicant needs this AND the server's own recomputation of the prefill to
   * agree, so omitting it writes nothing back rather than writing the wrong
   * thing.
   */
  edited_keys?: EditedKeys;
}

/** Create a draft offer + render its preview (AXHE-3718 create). */
export const useCreateOffer = (applicantExternalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: OfferValuesInput) =>
      OfferLettersService.postInternalApiApplicantsOfferLetters(applicantExternalId, input),
    [applicantExternalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/** Edit an existing offer's values + re-render (AXHE-3718 update). */
export const useUpdateOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: Omit<OfferValuesInput, 'offer_letter_template_id'>) =>
      OfferLettersService.patchInternalApiOfferLetters(externalId, input),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/** Submit per the resolved CTA — send-for-approval vs send-to-candidate. */
export const useSubmitOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersSubmitForApproval(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Cancel an offer outright (AXHE-4682): a sent offer's signing request is
 * voided, a pending approval request is withdrawn, and the offer is marked
 * canceled. The response is the updated preview payload.
 */
export const useCancelOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersCancel(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Open a revision of a sent offer (AXHE-4425): a new draft that supersedes the
 * delivered document, pre-populated with its values and rendered from the
 * frozen document the applicant is holding rather than the live template. The
 * response is the revision's own preview payload.
 *
 * Nothing the applicant can see changes here. The predecessor stays `sent` and
 * stays signable until the revision is re-sent through `useSubmitOffer`, which
 * is what retires it — the ticket's "at that moment, and not before".
 *
 * Idempotent by design: pressing Edit again while a revision is open returns
 * that revision instead of minting a rival draft, so a double-click cannot
 * orphan what the recruiter already typed.
 */
export const useReviseOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersRevise(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Throw an unsent revision away (AXHE-4425), as if Edit had never been pressed.
 * The offer it supersedes is untouched and stays live for the applicant.
 *
 * Returns nothing — a 204. The endpoint accepts ONLY an unsent revision, and it
 * is deliberately not idempotent: every read path is scoped past the soft
 * delete, so a second DELETE on the same id is a 404. The container treats that
 * 404 as "already discarded" rather than retrying, which is why this hook
 * reports the outcome instead of flashing it.
 */
export type DiscardRevisionResult = { ok: true } | { ok: false; message: string };

export const useDiscardRevision = (externalId: string) => {
  const intl = useIntl();
  const [isDiscarding, setIsDiscarding] = useState(false);

  const discardRevision = useCallback(async (): Promise<DiscardRevisionResult> => {
    setIsDiscarding(true);
    try {
      await OfferLettersService.deleteInternalApiOfferLetters(externalId);
      return { ok: true };
    } catch (error) {
      // A 404 means the revision is already gone — from a retry, or from a
      // second tab. The edit the recruiter asked to drop IS dropped, so this
      // reports success rather than putting a failure in front of them for an
      // outcome they already have.
      if ((error as { status?: number } | null)?.status === 404) return { ok: true };
      return {
        ok: false,
        message:
          serverErrorMessage((error as { body?: unknown } | null)?.body) ??
          intl.formatMessage(messages.discardRevisionError),
      };
    } finally {
      setIsDiscarding(false);
    }
  }, [externalId, intl]);

  return { discardRevision, isDiscarding };
};
